import { useCallback, useEffect, useMemo, useState, ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { FiAlertCircle, FiEdit2, FiShield, FiUserCheck, FiUsers, FiX } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  fetchHrDirectory,
  fetchHrOrg,
  updateHrEmployeeCard,
} from "../../store/hr/hrSlice";
import { HrDirectoryEmployee, HrProfile } from "../../types/HrTypes";
import { useHrAccess } from "../../hooks/useHrAccess";
import { HR_DIRECTORY_EMP_ROLES } from "../../utils/employeeRoles";
import RolesOverviewModal from "../../components/hr/RolesOverviewModal";
import {
  AdminClearFilters,
  AdminMetaRow,
  AdminPageHeader,
  AdminPageShell,
  AdminPrimaryButton,
  AdminSearchInput,
  AdminSelect,
  AdminSoftInput,
  AdminSoftSelect,
  AdminTableCard,
  AdminTableState,
  adminTableCell,
  adminTableHead,
} from "../../components/admin/AdminPageShell";
import { CustomDatePicker } from "../../components/common/DatePickers";
import { normalizeWeekOffDays, WEEK_OFF_OPTIONS } from "../../utils/weekOff";

const emptyProfile = (emp: HrDirectoryEmployee): Record<string, unknown> => ({
  emp_id: emp.emp_id,
  emp_name: emp.emp_name,
  email: emp.email,
  role: emp.role,
  verified: emp.verified,
  efficiency: emp.efficiency,
  shift: emp.shift,
  employee_code: emp.profile?.employee_code || "",
  phone: emp.profile?.phone || "",
  alt_phone: emp.profile?.alt_phone || "",
  date_of_birth: emp.profile?.date_of_birth || "",
  gender: emp.profile?.gender || "",
  blood_group: emp.profile?.blood_group || "",
  marital_status: emp.profile?.marital_status || "",
  nationality: emp.profile?.nationality || "",
  father_name: emp.profile?.father_name || "",
  aadhaar_number: emp.profile?.aadhaar_number || "",
  last_date: emp.profile?.last_date || "",
  training_period: emp.profile?.training_period || "",
  address: emp.profile?.address || "",
  permanent_address: emp.profile?.permanent_address || emp.profile?.address || "",
  current_address: emp.profile?.current_address || "",
  city: emp.profile?.city || "",
  state: emp.profile?.state || "",
  pincode: emp.profile?.pincode || "",
  emergency_name: emp.profile?.emergency_name || "",
  emergency_phone: emp.profile?.emergency_phone || "",
  emergency_relation: emp.profile?.emergency_relation || "",
  department_id: emp.profile?.department_id || "",
  designation_id: emp.profile?.designation_id || "",
  joining_date: emp.profile?.joining_date || "",
  confirmation_date: emp.profile?.confirmation_date || "",
  employment_type: emp.profile?.employment_type || "full_time",
  employment_status:
    emp.profile?.employment_status === "ex_employee" ||
    emp.profile?.employment_status === "resigned" ||
    emp.profile?.employment_status === "terminated"
      ? "ex_employee"
      : emp.profile?.employment_status || "active",
  work_location: emp.profile?.work_location || "",
  week_off_days: normalizeWeekOffDays(emp.profile?.week_off_days),
  punch_mapping_id: emp.punch_mapping_id || "",
  bank_name: emp.profile?.bank_name || "",
  bank_account: emp.profile?.bank_account || "",
  ifsc: emp.profile?.ifsc || "",
  pan: emp.profile?.pan || "",
  uan: emp.profile?.uan || "",
  notes: emp.profile?.notes || "",
});

function empStatusLabel(status?: string | null) {
  if (status === "ex_employee" || status === "resigned" || status === "terminated") {
    return "Ex-employee";
  }
  return "Currently working";
}

const Directory = () => {
  const dispatch = useAppDispatch();
  const { canEditEmployeeRecords } = useHrAccess();
  const canEdit = canEditEmployeeRecords;
  const { directory, departments, designations, shifts, loading, error, saving } =
    useAppSelector((state) => state.hr);

  const [search, setSearch] = useState("");
  const [rolesOpen, setRolesOpen] = useState(false);
  const [status, setStatus] = useState("all");
  const [dept, setDept] = useState("");
  const [editing, setEditing] = useState<HrDirectoryEmployee | null>(null);
  const [form, setForm] = useState<Record<string, unknown>>({});
  const [tab, setTab] = useState<"job" | "personal" | "bank">("job");

  useEffect(() => {
    dispatch(fetchHrOrg());
    dispatch(fetchHrDirectory());
  }, [dispatch]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return directory
      .filter((emp) => {
        const profile = emp.profile;
        const matchesSearch =
          !q ||
          emp.emp_name.toLowerCase().includes(q) ||
          emp.email.toLowerCase().includes(q) ||
          emp.role.toLowerCase().includes(q) ||
          String(profile?.employee_code || "").toLowerCase().includes(q);
        const matchesDept =
          !dept || String(profile?.department_id || "") === dept;
        const matchesStatus =
          status === "all" ||
          (status === "verified" && emp.verified) ||
          (status === "pending" && !emp.verified) ||
          (status === "working" &&
            !["ex_employee", "resigned", "terminated"].includes(
              String(profile?.employment_status || "active"),
            )) ||
          (status === "ex_employee" &&
            ["ex_employee", "resigned", "terminated"].includes(
              String(profile?.employment_status || ""),
            )) ||
          (status === "on_notice" && profile?.employment_status === "on_notice");
        return matchesSearch && matchesDept && matchesStatus;
      })
      .sort((a, b) => {
        if (a.verified !== b.verified) return a.verified ? -1 : 1;
        return a.emp_name.localeCompare(b.emp_name);
      });
  }, [directory, search, dept, status]);

  const openEdit = (emp: HrDirectoryEmployee) => {
    setEditing(emp);
    setForm(emptyProfile(emp));
    setTab("job");
  };

  const setField = (key: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    const result = await dispatch(updateHrEmployeeCard(form));
    if (updateHrEmployeeCard.fulfilled.match(result)) {
      setEditing(null);
      dispatch(fetchHrDirectory());
    }
  };

  const hasFilters = Boolean(search.trim()) || status !== "all" || Boolean(dept);
  const closeRoles = useCallback(() => setRolesOpen(false), []);

  return (
    <>
      <Helmet>
        <title>People Directory</title>
      </Helmet>
      <AdminPageShell accent="#345952">
        <AdminPageHeader
          title="People directory"
          subtitle="Employee records live here: name, role, shift, efficiency, and verification, plus the HR profile."
        >
          <button
            type="button"
            onClick={() => setRolesOpen(true)}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-white px-3 py-2.5 text-sm font-semibold text-[#345952] shadow-sm hover:bg-[#E8F0EE]"
          >
            <FiShield size={16} />
            Roles overview
          </button>
          <AdminSearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search name, email, code…"
          />
          <AdminSelect value={dept} onChange={setDept}>
            <option value="">All departments</option>
            {departments.map((d) => (
              <option key={d.department_id} value={String(d.department_id)}>
                {d.name}
              </option>
            ))}
          </AdminSelect>
          <AdminSelect value={status} onChange={setStatus}>
            <option value="all">All statuses</option>
            <option value="verified">Verified</option>
            <option value="pending">Pending verify</option>
            {canEdit ? (
              <>
                <option value="working">Currently working</option>
                <option value="ex_employee">Ex-employee</option>
                <option value="on_notice">On notice</option>
              </>
            ) : null}
          </AdminSelect>
        </AdminPageHeader>

        <section className="grid gap-4 sm:grid-cols-3">
          {[
            { label: "People", value: directory.length, icon: FiUsers, tint: "bg-[#E8F0EE] text-[#345952]" },
            {
              label: "Verified",
              value: directory.filter((e) => e.verified).length,
              icon: FiUserCheck,
              tint: "bg-emerald-50 text-emerald-600",
            },
            {
              label: "On shift",
              value: directory.filter((e) => e.active).length,
              icon: FiShield,
              tint: "bg-violet-50 text-violet-600",
            },
          ].map(({ label, value, icon: Icon, tint }) => (
            <div
              key={label}
              className="flex items-center gap-4 rounded-2xl border border-[#E8E8E4] bg-white p-4 shadow-sm"
            >
              <div className={`rounded-xl p-3 ${tint}`}>
                <Icon size={22} />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#4A4A48]">{value}</p>
                <p className="text-sm text-[#8A8A85]">{label}</p>
              </div>
            </div>
          ))}
        </section>

        <AdminMetaRow
          left={
            <>
              <span>
                Showing {filtered.length} of {directory.length}
              </span>
              {hasFilters ? (
                <AdminClearFilters
                  onClick={() => {
                    setSearch("");
                    setStatus("all");
                    setDept("");
                  }}
                />
              ) : null}
            </>
          }
        />

        {error ? (
          <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <FiAlertCircle />
            {error}
          </div>
        ) : null}

        <AdminTableCard>
          <table className="table-auto w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-[#E8E8E4] bg-[#FDFBF2]">
                <th className={adminTableHead}>Employee</th>
                <th className={adminTableHead}>Code</th>
                <th className={adminTableHead}>Department</th>
                <th className={adminTableHead}>Designation</th>
                <th className={adminTableHead}>Shift</th>
                <th className={adminTableHead}>Status</th>
                <th className={`${adminTableHead} text-right`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && directory.length === 0 ? (
                <AdminTableState colSpan={7} variant="loading" message="Loading people…" />
              ) : filtered.length === 0 ? (
                <AdminTableState colSpan={7} variant="empty" message="No people found" />
              ) : (
                filtered.map((emp) => {
                  const profile = emp.profile as HrProfile | null;
                  return (
                    <tr
                      key={emp.emp_id}
                      className={`border-b border-[#F0F0EC] hover:bg-[#FAFAF8] ${
                        emp.verified ? "" : "opacity-50"
                      }`}
                    >
                      <td className={adminTableCell}>
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#E8F0EE] font-semibold text-[#345952]">
                            {emp.emp_name
                              .split(" ")
                              .map((p) => p[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-[#4A4A48]">{emp.emp_name}</p>
                            <p className="text-xs text-[#8A8A85]">{emp.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className={adminTableCell}>
                        {profile?.employee_code || "—"}
                      </td>
                      <td className={adminTableCell}>
                        {profile?.department?.name || "—"}
                      </td>
                      <td className={adminTableCell}>
                        {profile?.designation?.name || emp.role}
                      </td>
                      <td className={adminTableCell}>{emp.shift || "—"}</td>
                      <td className={adminTableCell}>
                        <div className="flex flex-col gap-1">
                          <span
                            className={`inline-flex w-fit rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                              emp.verified
                                ? "bg-emerald-50 text-emerald-700"
                                : "bg-amber-50 text-amber-700"
                            }`}
                          >
                            {emp.verified ? "Verified" : "Pending"}
                          </span>
                          <span className="text-xs text-[#8A8A85]">
                            {canEdit
                              ? empStatusLabel(profile?.employment_status)
                              : "Restricted"}
                            {emp.active ? " · on shift" : ""}
                          </span>
                        </div>
                      </td>
                      <td className={`${adminTableCell} text-right`}>
                        <button
                          type="button"
                          onClick={() => openEdit(emp)}
                          className="inline-flex items-center gap-1.5 rounded-xl border border-[#DDDDD8] px-3 py-2.5 text-xs font-medium text-[#4A4A48] hover:border-[#345952] hover:bg-[#E8F0EE] hover:text-[#345952]"
                        >
                          <FiEdit2 />
                          {canEdit ? "Manage" : "View"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </AdminTableCard>
      </AdminPageShell>

      <RolesOverviewModal open={rolesOpen} onClose={closeRoles} />

      {editing ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 p-4">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-5 shadow-xl">
            <div className="mb-4 flex items-start justify-between gap-3">
              <div>
                <h2 className="text-lg font-semibold text-[#4A4A48]">
                  {editing.emp_name}
                </h2>
                <p className="text-sm text-[#8A8A85]">{editing.email}</p>
              </div>
              <button type="button" onClick={() => setEditing(null)}>
                <FiX className="text-[#8A8A85]" />
              </button>
            </div>

            <div className="mb-4 flex gap-2">
              {(["job", "personal", "bank"] as const).map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={`rounded-lg px-3 py-1.5 text-xs font-medium capitalize ${
                    tab === t
                      ? "bg-[#345952] text-white"
                      : "bg-[#F0F0EC] text-[#4A4A48]"
                  }`}
                >
                  {t === "job" ? "Job" : t === "personal" ? "Personal" : "Bank & IDs"}
                </button>
              ))}
            </div>

            {tab === "job" ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Name">
                  <AdminSoftInput
                    disabled={!canEdit}
                    value={String(form.emp_name || "")}
                    onChange={(e) => setField("emp_name", e.target.value)}
                  />
                </Field>
                <Field label="Employee code">
                  <AdminSoftInput
                    disabled={!canEdit}
                    value={String(form.employee_code || "")}
                    onChange={(e) => setField("employee_code", e.target.value)}
                  />
                </Field>
                <Field label="Role">
                  <AdminSoftSelect
                    disabled={!canEdit}
                    value={String(form.role || "")}
                    onChange={(e) => setField("role", e.target.value)}
                  >
                    {HR_DIRECTORY_EMP_ROLES.map(
                      (r) => (
                        <option key={r}>{r}</option>
                      ),
                    )}
                  </AdminSoftSelect>
                </Field>
                <Field label="Shift">
                  <AdminSoftSelect
                    disabled={!canEdit}
                    value={String(form.shift || "")}
                    onChange={(e) => setField("shift", e.target.value)}
                  >
                    <option value="">Select shift</option>
                    {shifts.map((s) => (
                      <option key={s.shift_id} value={s.shift_name}>
                        {s.shift_name}
                      </option>
                    ))}
                  </AdminSoftSelect>
                </Field>
                <Field label="Department">
                  <AdminSoftSelect
                    disabled={!canEdit}
                    value={String(form.department_id || "")}
                    onChange={(e) => setField("department_id", e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">Unassigned</option>
                    {departments.map((d) => (
                      <option key={d.department_id} value={d.department_id}>
                        {d.name}
                      </option>
                    ))}
                  </AdminSoftSelect>
                </Field>
                <Field label="Designation">
                  <AdminSoftSelect
                    disabled={!canEdit}
                    value={String(form.designation_id || "")}
                    onChange={(e) =>
                      setField("designation_id", e.target.value ? Number(e.target.value) : null)
                    }
                  >
                    <option value="">Unassigned</option>
                    {designations.map((d) => (
                      <option key={d.designation_id} value={d.designation_id}>
                        {d.name}
                      </option>
                    ))}
                  </AdminSoftSelect>
                </Field>
                <Field label="Employment type">
                  <AdminSoftSelect
                    disabled={!canEdit}
                    value={String(form.employment_type || "full_time")}
                    onChange={(e) => setField("employment_type", e.target.value)}
                  >
                    <option value="full_time">Full time</option>
                    <option value="part_time">Part time</option>
                    <option value="intern">Intern</option>
                    <option value="contract">Contract</option>
                  </AdminSoftSelect>
                </Field>
                {canEdit ? (
                  <Field label="Emp status">
                    <AdminSoftSelect
                      disabled={!canEdit}
                      value={
                        form.employment_status === "ex_employee"
                          ? "ex_employee"
                          : "active"
                      }
                      onChange={(e) => setField("employment_status", e.target.value)}
                    >
                      <option value="active">Currently working</option>
                      <option value="ex_employee">Ex-employee</option>
                    </AdminSoftSelect>
                  </Field>
                ) : null}
                <Field label="Joining date">
                  <CustomDatePicker
                    disabled={!canEdit}
                    value={String(form.joining_date || "")}
                    onChange={(iso) => setField("joining_date", iso)}
                    placeholder="Select joining date"
                  />
                </Field>
                {canEdit ? (
                  <>
                    <Field label="Last date">
                      <CustomDatePicker
                        disabled={!canEdit}
                        value={String(form.last_date || "")}
                        onChange={(iso) => setField("last_date", iso)}
                        placeholder="Select last date"
                      />
                    </Field>
                    <Field label="Training period">
                      <AdminSoftInput
                        disabled={!canEdit}
                        placeholder="e.g. 3 months"
                        value={String(form.training_period || "")}
                        onChange={(e) => setField("training_period", e.target.value)}
                      />
                    </Field>
                  </>
                ) : null}
                <Field label="Work location">
                  <AdminSoftSelect
                    disabled={!canEdit}
                    value={(() => {
                      const loc = String(form.work_location || "Office").toLowerCase();
                      if (loc === "remote") return "Remote";
                      if (loc === "hybrid") return "Hybrid";
                      return "Office";
                    })()}
                    onChange={(e) => setField("work_location", e.target.value)}
                  >
                    <option value="Office">Office</option>
                    <option value="Remote">Remote</option>
                    <option value="Hybrid">Hybrid</option>
                  </AdminSoftSelect>
                  <p className="mt-1.5 text-[11px] leading-4 text-[#8A8A85]">
                    Office: punch in (Admin / Executive / Super Admin can still mark them from
                    Attendance). Remote: self mark from portal. Hybrid: self mark and punch.
                  </p>
                </Field>
                {canEdit ? (
                  <Field label="Punch machine ID">
                    <AdminSoftInput
                      disabled={!canEdit}
                      placeholder="e.g. 00000028"
                      value={String(form.punch_mapping_id || "")}
                      onChange={(e) => setField("punch_mapping_id", e.target.value)}
                    />
                    <p className="mt-1.5 text-[11px] leading-4 text-[#8A8A85]">
                      Enrollment ID from the punch device. Clear to unmap. If this ID was on
                      another employee, it moves to this person.
                    </p>
                  </Field>
                ) : null}
                <Field label="Week offs" className="sm:col-span-2">
                  <div className="flex flex-wrap gap-2 pt-1">
                    {WEEK_OFF_OPTIONS.map(({ day, label, locked }) => {
                      const selected = normalizeWeekOffDays(form.week_off_days).includes(day);
                      const disabled = !canEdit || Boolean(locked);
                      return (
                        <button
                          key={day}
                          type="button"
                          disabled={disabled}
                          title={
                            locked
                              ? "Sunday is always week off for everyone"
                              : selected
                                ? `Remove ${label}`
                                : `Add ${label}`
                          }
                          onClick={() => {
                            if (locked) return;
                            const current = normalizeWeekOffDays(form.week_off_days);
                            const next = current.includes(day)
                              ? current.filter((d) => d !== day)
                              : [...current, day];
                            setField("week_off_days", normalizeWeekOffDays(next));
                          }}
                          className={[
                            "rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition",
                            selected
                              ? "border-[#345952] bg-[#345952] text-white"
                              : "border-[#DDDDD8] bg-white text-[#4A4A48] hover:border-[#345952]",
                            disabled && !selected ? "opacity-60" : "",
                            locked ? "cursor-default" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  <p className="mt-1.5 text-[11px] leading-4 text-[#8A8A85]">
                    Sunday is always off. Add Sat, Mon, etc. for employees with extra week offs.
                    Attendance marks these days automatically.
                  </p>
                </Field>
                <Field label="Efficiency (units/day)">
                  <AdminSoftInput
                    type="number"
                    disabled={!canEdit}
                    value={String(form.efficiency ?? 0)}
                    onChange={(e) => setField("efficiency", Number(e.target.value))}
                  />
                </Field>
                <Field label="Verified">
                  <AdminSoftSelect
                    disabled={!canEdit}
                    value={form.verified ? "1" : "0"}
                    onChange={(e) => setField("verified", e.target.value === "1")}
                  >
                    <option value="1">Verified</option>
                    <option value="0">Pending</option>
                  </AdminSoftSelect>
                </Field>
              </div>
            ) : null}

            {tab === "personal" ? (
              canEdit ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Contact number">
                  <AdminSoftInput
                    disabled={!canEdit}
                    value={String(form.phone || "")}
                    onChange={(e) => setField("phone", e.target.value)}
                  />
                </Field>
                <Field label="Emergency contact number">
                  <AdminSoftInput
                    disabled={!canEdit}
                    value={String(form.emergency_phone || "")}
                    onChange={(e) => setField("emergency_phone", e.target.value)}
                  />
                </Field>
                <Field label="Father name">
                  <AdminSoftInput
                    disabled={!canEdit}
                    value={String(form.father_name || "")}
                    onChange={(e) => setField("father_name", e.target.value)}
                  />
                </Field>
                <Field label="Date of birth">
                  <CustomDatePicker
                    disabled={!canEdit}
                    value={String(form.date_of_birth || "")}
                    onChange={(iso) => setField("date_of_birth", iso)}
                    placeholder="Select date of birth"
                  />
                </Field>
                <Field label="Aadhaar number">
                  <AdminSoftInput
                    disabled={!canEdit}
                    value={String(form.aadhaar_number || "")}
                    onChange={(e) => setField("aadhaar_number", e.target.value)}
                  />
                </Field>
                <Field label="PAN number">
                  <AdminSoftInput
                    disabled={!canEdit}
                    value={String(form.pan || "")}
                    onChange={(e) => setField("pan", e.target.value)}
                  />
                </Field>
                <Field label="Emergency contact name">
                  <AdminSoftInput
                    disabled={!canEdit}
                    value={String(form.emergency_name || "")}
                    onChange={(e) => setField("emergency_name", e.target.value)}
                  />
                </Field>
                <Field label="Gender">
                  <AdminSoftInput
                    disabled={!canEdit}
                    value={String(form.gender || "")}
                    onChange={(e) => setField("gender", e.target.value)}
                  />
                </Field>
                <Field label="Permanent home address" className="sm:col-span-2">
                  <AdminSoftInput
                    disabled={!canEdit}
                    value={String(form.permanent_address || "")}
                    onChange={(e) => setField("permanent_address", e.target.value)}
                  />
                </Field>
                <Field label="Current residential home address" className="sm:col-span-2">
                  <AdminSoftInput
                    disabled={!canEdit}
                    value={String(form.current_address || "")}
                    onChange={(e) => setField("current_address", e.target.value)}
                  />
                </Field>
                <Field label="City">
                  <AdminSoftInput
                    disabled={!canEdit}
                    value={String(form.city || "")}
                    onChange={(e) => setField("city", e.target.value)}
                  />
                </Field>
                <Field label="State">
                  <AdminSoftInput
                    disabled={!canEdit}
                    value={String(form.state || "")}
                    onChange={(e) => setField("state", e.target.value)}
                  />
                </Field>
              </div>
              ) : (
                <p className="rounded-xl border border-[#E8E8E4] bg-[#FAFAF8] p-4 text-sm text-[#8A8A85]">
                  Personal details are confidential. Only Admin, Executive, or Super Admin can view them.
                </p>
              )
            ) : null}

            {tab === "bank" ? (
              canEdit ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Bank name">
                  <AdminSoftInput disabled={!canEdit} value={String(form.bank_name || "")} onChange={(e) => setField("bank_name", e.target.value)} />
                </Field>
                <Field label="Account">
                  <AdminSoftInput disabled={!canEdit} value={String(form.bank_account || "")} onChange={(e) => setField("bank_account", e.target.value)} />
                </Field>
                <Field label="IFSC">
                  <AdminSoftInput disabled={!canEdit} value={String(form.ifsc || "")} onChange={(e) => setField("ifsc", e.target.value)} />
                </Field>
                <Field label="UAN">
                  <AdminSoftInput disabled={!canEdit} value={String(form.uan || "")} onChange={(e) => setField("uan", e.target.value)} />
                </Field>
                <Field label="Notes" className="sm:col-span-2">
                  <AdminSoftInput disabled={!canEdit} value={String(form.notes || "")} onChange={(e) => setField("notes", e.target.value)} />
                </Field>
              </div>
              ) : (
                <p className="rounded-xl border border-[#E8E8E4] bg-[#FAFAF8] p-4 text-sm text-[#8A8A85]">
                  Bank and ID details are confidential. Only Admin, Executive, or Super Admin can view them.
                </p>
              )
            ) : null}

            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-xl border border-[#DDDDD8] px-4 py-2.5 text-xs font-medium"
              >
                Close
              </button>
              {canEdit ? (
                <AdminPrimaryButton disabled={saving} onClick={save}>
                  Save
                </AdminPrimaryButton>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

function Field({
  label,
  children,
  className = "",
}: {
  label: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <label className={`flex flex-col gap-1 text-xs text-[#8A8A85] ${className}`}>
      {label}
      {children}
    </label>
  );
}

export default Directory;
